import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { PPEItem, ItemType, AssignableUser } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useOrgConfig } from '../contexts/OrgConfigContext';
import { downloadCSV } from '../utils/export';

const conditionColors: Record<string, string> = { good: '#16a34a', fair: '#ca8a04', poor: '#ea580c', out_of_service: '#dc2626' };
const conditionLabels: Record<string, string> = { good: 'Good', fair: 'Fair', poor: 'Poor', out_of_service: 'Out of Service' };

interface InspectionStatus {
  year: number;
  completed: boolean;
  inspection: { id: string; completed_at: string; notes: string } | null;
}

export default function Inventory({ retired = false }: { retired?: boolean }) {
  const { user } = useAuth();
  const { userCanAdd } = useOrgConfig();
  const navigate = useNavigate();
  const canManageGear = user?.role === 'admin' || user?.role === 'quartermaster';
  const isStandardUser = user?.role === 'user';

  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<PPEItem[]>([]);
  const [itemTypes, setItemTypes] = useState<ItemType[]>([]);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Sync filter state with URL search params
  const search = searchParams.get('q') || '';
  const filterType = searchParams.get('type') || '';
  const filterClassification = searchParams.get('class') || '';
  const filterExpiryYear = searchParams.get('expires') || '';

  const setFilter = (key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  };

  // Inspection state for standard users
  const currentYear = new Date().getFullYear();
  const [inspectionStatus, setInspectionStatus] = useState<InspectionStatus | null>(null);
  const [completing, setCompleting] = useState(false);
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [inspectionError, setInspectionError] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('retired', retired ? 'true' : 'false');
      if (search) params.set('search', search);
      if (filterType) params.set('item_type_id', filterType);
      if (filterClassification) params.set('gear_classification', filterClassification);
      if (filterExpiryYear) params.set('expires_year', filterExpiryYear);

      const data = await api.get<PPEItem[]>(`/ppe-items?${params}`);
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterClassification, filterExpiryYear, retired]);

  useEffect(() => {
    fetchItems();
    api.get<ItemType[]>('/item-types').then(setItemTypes).catch(console.error);
    api.get<AssignableUser[]>('/users/assignable').then(setUsers).catch(console.error);
    // Fetch inspection status for standard users
    if (!retired) {
      api.get<InspectionStatus>(`/inspections/me?year=${currentYear}`)
        .then(setInspectionStatus)
        .catch(console.error);
    }
  }, [fetchItems, currentYear, retired]);

  // Debounced search — syncs input to URL param
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setFilter('q', searchInput), 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const expiryYearOptions = [currentYear, currentYear + 1, currentYear + 2, currentYear + 3, currentYear + 4, currentYear + 5];

  const handleCompleteInspection = async () => {
    setCompleting(true);
    setInspectionError('');
    try {
      const data = await api.post<InspectionStatus>('/inspections/me/complete', { year: currentYear, notes: inspectionNotes });
      setInspectionStatus(data);
      setInspectionNotes('');
    } catch (err: any) {
      setInspectionError(err.message);
    } finally {
      setCompleting(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const };
  const selectStyle = { ...inputStyle, background: 'white' };
  const thStyle = { padding: '12px 16px', textAlign: 'left' as const, fontWeight: 600, color: '#374151' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111' }}>
          {retired ? 'Retired Items' : 'PPE Inventory'}
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {items.length > 0 && (
            <button onClick={() => {
              const headers = ['Item Type', 'Serial Number', 'Manufacturer', 'Model', 'Size', 'Condition', 'Classification', 'Assigned To', 'Station', 'Date Manufactured', 'Notes'];
              const rows = items.map(i => [
                i.item_type_name, i.serial_number || '', i.manufacturer || '', i.model || '', i.size || '',
                i.condition, i.gear_classification || '', i.assigned_first_name ? `${i.assigned_first_name} ${i.assigned_last_name}` : '',
                i.station || '', i.date_manufactured || '', i.notes || '',
              ]);
              downloadCSV(headers, rows, `ppe-inventory-${retired ? 'retired' : 'active'}.csv`);
            }}
              style={{ background: '#f3f4f6', border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
              Export CSV
            </button>
          )}
          {!retired && userCanAdd && (
            <button onClick={() => setShowForm(true)}
              style={{ background: '#b91c1c', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              + Add Item
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
        <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search serial, manufacturer, name..."
          style={inputStyle} />
        <select value={filterType} onChange={e => setFilter('type', e.target.value)} style={selectStyle}>
          <option value="">All Types</option>
          {itemTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {!retired && (
          <select value={filterClassification} onChange={e => setFilter('class', e.target.value)} style={selectStyle}>
            <option value="">Frontline / Backup</option>
            <option value="frontline">Frontline</option>
            <option value="backup">Backup</option>
            <option value="unset">Not Set</option>
          </select>
        )}
        {!retired && (
          <select value={filterExpiryYear} onChange={e => setFilter('expires', e.target.value)} style={selectStyle}>
            <option value="">All Expiry Years</option>
            {expiryYearOptions.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
        )}
      </div>

      {/* Items table / cards */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading...</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#666', background: 'white', borderRadius: 12 }}>
          {retired ? 'No retired items' : 'No items found. Add your first PPE item!'}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="desktop-table" style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Serial #</th>
                  <th style={thStyle}>Brand</th>
                  <th style={thStyle}>Mfg Date</th>
                  {canManageGear && <th style={thStyle}>Assigned To</th>}
                  {!canManageGear && <th style={thStyle}>Condition</th>}
                  {retired && <th style={thStyle}>Retired</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const classLabel = item.gear_classification === 'frontline' ? 'Frontline'
                    : item.gear_classification === 'backup' ? 'Backup' : null;
                  const classColor = item.gear_classification === 'frontline' ? '#1d4ed8'
                    : item.gear_classification === 'backup' ? '#ca8a04' : '';
                  const mfgDate = item.date_manufactured
                    ? new Date(item.date_manufactured).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    : '-';

                  return (
                    <tr key={item.id} onClick={() => navigate(`/inventory/${item.id}`)}
                      style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                        {item.item_type_name}
                        {classLabel && (
                          <span style={{ color: classColor, fontWeight: 500, fontSize: 12, marginLeft: 6 }}>
                            ({classLabel})
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 13 }}>{item.serial_number || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>{item.manufacturer || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13 }}>{mfgDate}</td>
                      {canManageGear && (
                        <td style={{ padding: '12px 16px' }}>
                          {item.assigned_first_name && item.assigned_to ? (
                            <Link to={`/members/${item.assigned_to}/gear`}
                              style={{ color: '#1e40af', textDecoration: 'none', fontWeight: 500 }}
                              onClick={e => e.stopPropagation()}>
                              {item.assigned_first_name} {item.assigned_last_name}
                            </Link>
                          ) : '-'}
                          {item.assigned_badge && <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 4 }}>({item.assigned_badge})</span>}
                        </td>
                      )}
                      {!canManageGear && (
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ background: conditionColors[item.condition], color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                            {conditionLabels[item.condition]}
                          </span>
                        </td>
                      )}
                      {retired && <td style={{ padding: '12px 16px', fontSize: 12 }}>{item.retired_at ? new Date(item.retired_at).toLocaleDateString() : '-'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mobile-cards" style={{ display: 'none' }}>
            {items.map(item => {
              const classLabel = item.gear_classification === 'frontline' ? 'Frontline'
                : item.gear_classification === 'backup' ? 'Backup' : null;
              const classColor = item.gear_classification === 'frontline' ? '#1d4ed8'
                : item.gear_classification === 'backup' ? '#ca8a04' : '';

              return (
                <div key={item.id} onClick={() => navigate(`/inventory/${item.id}`)}
                  style={{ background: 'white', borderRadius: 10, padding: 14, marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>{item.item_type_name}</span>
                      {classLabel && (
                        <span style={{ color: classColor, fontSize: 12, fontWeight: 500 }}>({classLabel})</span>
                      )}
                    </div>
                    <span style={{ background: conditionColors[item.condition], color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                      {conditionLabels[item.condition]}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#666' }}>
                    {item.serial_number && <div>SN: {item.serial_number}</div>}
                    {item.manufacturer && <div>{item.manufacturer} {item.model}</div>}
                    {item.date_manufactured && <div>Mfg: {new Date(item.date_manufactured).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>}
                    {item.assigned_first_name && canManageGear && <div>Assigned: {item.assigned_first_name} {item.assigned_last_name}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Annual Inspection Section (standard users, active inventory only) */}
      {isStandardUser && !retired && inspectionStatus && (
        <div style={{
          marginTop: 24,
          background: 'white', borderRadius: 12, padding: 20,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: inspectionStatus.completed ? '2px solid #16a34a' : '2px solid #f59e0b',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#111' }}>
                {currentYear} Annual Inspection
              </h2>
              {inspectionStatus.completed ? (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#16a34a', marginBottom: 4 }}>
                    &#10003; Complete
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    Completed {inspectionStatus.inspection?.completed_at
                      ? new Date(inspectionStatus.inspection.completed_at).toLocaleDateString()
                      : ''}
                  </div>
                  {inspectionStatus.inspection?.notes && (
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                      Notes: {inspectionStatus.inspection.notes}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#f59e0b', marginBottom: 4 }}>
                    Due &mdash; Please review your gear above and verify
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    Confirm that the information for your {items.length} item{items.length !== 1 ? 's' : ''} listed above is accurate and up to date.
                  </div>
                </div>
              )}
            </div>

            {!inspectionStatus.completed && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 260, maxWidth: 360 }}>
                {inspectionError && (
                  <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
                    {inspectionError}
                  </div>
                )}
                <input value={inspectionNotes} onChange={e => setInspectionNotes(e.target.value)}
                  placeholder="Optional notes (e.g. gloves showing wear)..."
                  style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                <button onClick={handleCompleteInspection} disabled={completing}
                  style={{
                    background: '#16a34a', color: 'white', border: 'none', padding: '12px 20px',
                    borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                    opacity: completing ? 0.7 : 1,
                  }}>
                  {completing ? 'Completing...' : 'Complete Annual Inspection'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showForm && (
        <AddItemModal
          itemTypes={itemTypes}
          users={users}
          canManageGear={canManageGear}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchItems(); }}
        />
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-table { display: none !important; }
          .mobile-cards { display: block !important; }
        }
      `}</style>
    </div>
  );
}

function AddItemModal({ itemTypes, users, canManageGear, onClose, onSaved }: {
  itemTypes: ItemType[]; users: AssignableUser[]; canManageGear: boolean;
  onClose: () => void; onSaved: () => void;
}) {
  const { userCanChangeClassification } = useOrgConfig();
  const [form, setForm] = useState<any>({
    item_type_id: itemTypes[0]?.id || '',
    manufacturer: '', model: '', size: '', serial_number: '',
    date_manufactured: '', condition: 'good',
    gear_classification: '',
    assigned_to: '', station: '', notes: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dateAnomalyConfirm, setDateAnomalyConfirm] = useState(false);

  const selectedType = itemTypes.find(t => t.id === form.item_type_id);

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f: any) => ({ ...f, [field]: e.target.value }));

  const checkDateAnomaly = async (): Promise<boolean> => {
    if (form.gear_classification !== 'backup' || !form.date_manufactured || !form.assigned_to) return false;
    try {
      const params = new URLSearchParams();
      params.set('retired', 'false');
      params.set('item_type_id', form.item_type_id);
      params.set('gear_classification', 'frontline');
      const items = await api.get<PPEItem[]>(`/ppe-items?${params}`);
      const frontlineForUser = items.filter(i => i.assigned_to === form.assigned_to);
      for (const fl of frontlineForUser) {
        if (fl.date_manufactured && form.date_manufactured < fl.date_manufactured.split('T')[0]) {
          return true;
        }
      }
    } catch { /* don't block save */ }
    return false;
  };

  const doSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/ppe-items', form);
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const hasAnomaly = await checkDateAnomaly();
      if (hasAnomaly) {
        setSaving(false);
        setDateAnomalyConfirm(true);
        return;
      }
      await api.post('/ppe-items', form);
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Show classification if the type tracks it AND the user has permission
  const showClassification = selectedType?.track_gear_classification && (canManageGear || userCanChangeClassification);

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Add PPE Item</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666' }}>x</button>
        </div>

        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Item Type *</label>
            <select value={form.item_type_id} onChange={update('item_type_id')} required style={{ ...inputStyle, background: 'white' }}>
              {itemTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {selectedType?.track_manufacturer && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Manufacturer</label>
                <input value={form.manufacturer} onChange={update('manufacturer')} style={inputStyle} />
              </div>
            )}
            {selectedType?.track_model && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Model</label>
                <input value={form.model} onChange={update('model')} style={inputStyle} />
              </div>
            )}
            {selectedType?.track_size && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Size</label>
                <input value={form.size} onChange={update('size')} style={inputStyle} />
              </div>
            )}
            {selectedType?.track_serial_number && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Serial Number</label>
                <input value={form.serial_number} onChange={update('serial_number')} style={inputStyle} />
              </div>
            )}
            {selectedType?.track_date_manufactured && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Date Manufactured</label>
                <input type="date" value={form.date_manufactured} onChange={update('date_manufactured')} style={inputStyle} />
              </div>
            )}
            {selectedType?.track_condition && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Condition</label>
                <select value={form.condition} onChange={update('condition')} style={{ ...inputStyle, background: 'white' }}>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
              </div>
            )}
            {selectedType?.track_station && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Station</label>
                <input value={form.station} onChange={update('station')} style={inputStyle} />
              </div>
            )}
          </div>

          {showClassification && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Classification</label>
              <select value={form.gear_classification} onChange={update('gear_classification')} style={{ ...inputStyle, background: 'white' }}>
                <option value="">Not Set</option>
                <option value="frontline">Frontline</option>
                <option value="backup">Backup</option>
              </select>
            </div>
          )}

          {selectedType?.track_assigned_to && canManageGear && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Assign To</label>
              <select value={form.assigned_to} onChange={update('assigned_to')} style={{ ...inputStyle, background: 'white' }}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} {u.badge_number ? `(${u.badge_number})` : ''}</option>)}
              </select>
            </div>
          )}

          {selectedType?.track_notes && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.notes} onChange={update('notes')} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: '10px 20px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Add Item'}
            </button>
          </div>
        </form>

        {/* Date Anomaly Confirmation Dialog */}
        {dateAnomalyConfirm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
            onClick={() => setDateAnomalyConfirm(false)}>
            <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 440, width: '100%' }}
              onClick={e => e.stopPropagation()}>
              <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, color: '#92400e' }}>Date Anomaly Detected</h2>
              <p style={{ fontSize: 14, color: '#374151', marginBottom: 16, lineHeight: 1.5 }}>
                This <strong>backup</strong> item has an earlier manufacture date than the corresponding <strong>frontline</strong> item for the same user and type.
              </p>
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
                Typically the backup should be newer than the frontline gear. Are you sure you want to save?
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setDateAnomalyConfirm(false)}
                  style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>
                  Go Back
                </button>
                <button onClick={() => { setDateAnomalyConfirm(false); doSave(); }}
                  style={{ padding: '10px 20px', background: '#92400e', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  Save Anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
